import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Venue } from '../../../types';
import type { PlaceBasicDetails } from '../../../api/places.api';
import AddressAutocomplete from '../../ui/AddressAutocomplete';
import { EATING_CATEGORIES } from './mealSlots';

interface Props {
  venues: Venue[];
  /** The linked venue, if any. Stays listed even when it is not a place to eat. */
  venueId: string | null;
  onSelectVenue: (venueId: string | null) => void;
  onPickPlace: (place: PlaceBasicDetails) => Promise<void>;
  /** Label of the empty option: "No place" when editing, "Pick a saved one" when adding. */
  emptyLabel: string;
  autoFocusSearch?: boolean;
}

/**
 * Where a meal happens: a saved place to eat, or one searched and saved to Places.
 *
 * ADR-0013 — only RESTAURANT/CAFE are offered, and the search is
 * AddressAutocomplete on purpose: it is the one that respects ADR-0001
 * (proxy, debounce, session token).
 */
export default function MealPlacePicker({
  venues, venueId, onSelectVenue, onPickPlace, emptyLabel, autoFocusSearch,
}: Props) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  // Without the linked one, opening the picker would show the empty option
  // over a meal that has a place.
  const options = useMemo(
    () => venues.filter(v => EATING_CATEGORIES.has(v.category) || v.id === venueId),
    [venues, venueId],
  );

  async function pick(place: PlaceBasicDetails) {
    await onPickPlace(place);
    setQuery('');
  }

  return (
    <div className="flex flex-col sm:flex-row gap-1.5">
      {options.length > 0 && (
        <select
          value={venueId ?? ''}
          onChange={e => onSelectVenue(e.target.value || null)}
          aria-label={t('meals.linkVenue')}
          className="input sm:!w-44 shrink-0 !text-xs !px-2 !py-1.5 cursor-pointer"
        >
          <option value="">{emptyLabel}</option>
          {options.map(v => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
      )}
      <div className="flex-1 min-w-0">
        <AddressAutocomplete
          value={query}
          onChange={setQuery}
          onSelect={pick}
          placeholder={t('meals.searchPlace')}
          className="!text-xs !px-2.5 !py-1.5"
          autoFocus={autoFocusSearch}
        />
      </div>
    </div>
  );
}
